import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import {  useInspectionReportSummary } from "api/inspection";
import colors from 'assets/theme/base/colors';
import Card from "@mui/material/Card";
import BaseTable from "components/DataTable/BaseTable";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatDateTime, getYearMonthString } from "utils/helper";
import { fetchInspectionReports } from "api/inspection";

const TABLE_COLUMNS = [
    { id: "report_id", Header: "Report Id", accessor: "report_id", align: "left", sortable: true },
    { id: "category", Header: "Category", accessor: "category", align: "left" },
    { id: "ride", Header: "Ride", accessor: "ride", align: "left" },
    { id: "type", Header: "Type", accessor: "type", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "user", Header: "Issued By", accessor: "user", align: "left", sortable: true },
];

export const parentRoute = '/inspections-reports';

const REPORT_FILTER_CONFIGS = [
    { id: 'hasError', label: 'Has Error', type: 'select', options: [{ value: 1, label: 'Yes' }, { value: 0, label: 'No' }], placeholder: 'All' },
    { id: 'date', label: 'Date', type: 'date', placeholder: 'Date' },
];

function InspectionsReportTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const calendarRef = useRef(null);
    const refreshKey = 0;
    const [baseTableParams, setBaseTableParams] = useState({});
    const [currentMonthYear, setCurrentMonthYear] = useState(getYearMonthString(new Date()));
    const [showCalendar] = useState(true);
    const calenadarWrapperRef = useRef();
    const savedState = location.state || { sort: "created_at", dir: "desc" };

    const summaryQuery = useInspectionReportSummary(currentMonthYear);

    const calendarEvents = useMemo(() => (summaryQuery.data || []).map(report => ({
        title: report.count + " reports",
        date: report.date,
        display: 'block',
        backgroundColor: '#9974C1',
        borderColor: colors.primary.dark,
        textColor: '#fff',
    })), [summaryQuery.data]);

    const createTableRow = useCallback((item) => ({
        report_id: <MDTypography component="div" variant="standard" color="text">
            {item.report_id ?? "-"}
            {item.late || item.inspection_checklist_skip_item?.length > 0 ? <span style={{ color: 'red', fontSize: 16 }}>{item.late ? " *" : " ≫"}</span> : null}
        </MDTypography>,
        category: <MDTypography component="div" variant="standard" color="text">{item.category?.name ?? "-"}</MDTypography>,
        ride: <MDTypography component="div" variant="standard" color="text">{item.ride?.name ?? "-"}</MDTypography>,
        type: <MDTypography component="div" variant="standard" color="text">{item.type?.name ?? "-"}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">{formatDateTime(item.created_at)}</MDTypography>,
        user: <MDTypography component="div" variant="standard" color="text">{item.user?.full_name ?? "-"}</MDTypography>,
    }), []);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Card ref={calenadarWrapperRef} sx={{ background: '#fff', p: 3, mb: 3 }}>
                {showCalendar && (
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin]}
                        initialView="dayGridMonth"
                        events={calendarEvents}
                        height="auto"
                        eventDisplay="block"
                        dayMaxEvents={false}
                        headerToolbar={{
                            left: 'title',
                            right: 'today customPrev customNext'
                        }}
                        customButtons={{
                            today: {
                                text: 'Today', click: function () {
                                    const calendarApi = calendarRef.current.getApi();
                                    calendarApi.today();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            },
                            customPrev: {
                                icon: 'chevron-left',
                                click: function () {
                                    const calendarApi = calendarRef.current.getApi();
                                    calendarApi.prev();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            },
                            customNext: {
                                icon: 'chevron-right',
                                click: function () {
                                    const calendarApi = calendarRef.current.getApi();
                                    calendarApi.next();
                                    const currentDate = calendarApi.getDate();
                                    setCurrentMonthYear(getYearMonthString(currentDate));
                                }
                            }
                        }}
                        eventClick={(info) => {
                            const clickedDate = info.event.startStr;
                            setBaseTableParams(prev => ({ ...prev, date: clickedDate, page: 1 }));
                            setTimeout(() => {
                                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                            }, 100);
                        }}
                    />
                )}
            </Card>
            <BaseTable
                key={refreshKey}
                queryKey={["inspection-reports"]}
                title="Inspections Reports"
                columns={TABLE_COLUMNS}
                fetchData={fetchInspectionReports}
                onParamsChange={setBaseTableParams}
                defaultState={savedState}
                createTableRow={createTableRow}
                filterConfigs={REPORT_FILTER_CONFIGS}
                showSearch
                showInspectionCategoryFilter
                showFrequencyTypeFilter
                searchPlaceholder="Search by report Id"
                onClickRow={(row, key) => { window.open(`${parentRoute}/${row.id}`, '_blank') }}
            />
        </DashboardLayout>
    );
}

export default InspectionsReportTable;
